import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import isFilledString from "~/helpers/isFilledString";
import isRecord from "~/helpers/isRecord";
import { EVENT_SCHEDULE_PATH } from "~produce/production/schedule/events/helpers/eventWeekScope";

export const EVENT_JOB_TARGET_KIND = "eventJob";

export interface EventJobScope {
	eventDetailId: null | string;
	jobNumber: number;
	productionJobId: string;
}

export const buildEventJobTarget = ({
	eventDetailId,
	jobNumber,
	productionJobId
}: EventJobScope): ContinuationTarget => ({
	targetId: JSON.stringify({ eventDetailId, jobNumber, productionJobId }),
	targetKind: EVENT_JOB_TARGET_KIND
});

const parseTargetId = (targetId: string): unknown => {
	try {
		return JSON.parse(targetId);
	} catch {
		return null;
	}
};

export const readEventJobScope = (target: ContinuationTarget | undefined): EventJobScope | null => {
	if (target?.targetKind !== EVENT_JOB_TARGET_KIND) return null;
	const scope = parseTargetId(target.targetId);
	if (!isRecord(scope)) return null;
	const { eventDetailId, jobNumber, productionJobId } = scope;
	if (!isFilledString(productionJobId) || typeof jobNumber !== "number" || !Number.isFinite(jobNumber)) return null;
	if (eventDetailId !== null && eventDetailId !== undefined && !isFilledString(eventDetailId)) return null;
	return { eventDetailId: eventDetailId ?? null, jobNumber, productionJobId };
};

export const withEventJobScope = (filterModel: unknown, scope: EventJobScope | null): unknown =>
	scope
		? {
				...((filterModel as Record<string, unknown> | null) ?? {}),
				productionJobId: { filterType: "set", values: [scope.productionJobId] }
			}
		: filterModel;

const eventJobReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: (target) => (readEventJobScope(target) ? EVENT_SCHEDULE_PATH : ""),
	matchesRoute: () => null
};

export default eventJobReceiverIdentityAdapter;
